import { DomainCard, marketingButtonVariants } from "@appcaire/ui";
import { Link } from "react-router-dom";
import { Home, Search, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { trackPageView } from "@/lib/analytics";
import { useTranslation } from "react-i18next";
import { LinkTo } from "@/components/site/link-to";
import { useResponseStatus } from "@/lib/ssr-status";

export const NotFound = () => {
  const { t } = useTranslation(["common", "home", "resources"]);

  // Make the SSR server respond with a real 404 (not a soft 404) for any route
  // that falls through to this component. No-op during client hydration.
  useResponseStatus(404);

  useEffect(() => {
    // Track 404 errors for monitoring
    trackPageView("/404", t("notFound.title"));
  }, [t]);

  return (
    <>
      <Helmet>
        <title>{t("notFound.title")} | Caire</title>
        <meta name="description" content={t("notFound.meta.description")} />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="flex items-center justify-center py-8 md:py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t("notFound.title")}
          </h1>
          <p className="text-xl text-caire-muted mb-12 max-w-2xl mx-auto">
            {t("notFound.description")}
          </p>

          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-12">
            <LinkTo
              to="home"
              className={marketingButtonVariants({
                variant: "primary",
                size: "band",
              })}
            >
              <Home className="w-5 h-5" />
              {t("notFound.home")}
            </LinkTo>
            <LinkTo
              to="contact"
              className={marketingButtonVariants({
                variant: "secondary",
                size: "band",
              })}
            >
              <Search className="w-5 h-5" />
              {t("notFound.contact")}
            </LinkTo>
            <button
              type="button"
              onClick={() => window.history.back()}
              className={marketingButtonVariants({
                variant: "secondary",
                size: "band",
              })}
            >
              <ArrowLeft className="w-5 h-5" />
              {t("notFound.back")}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <DomainCard domain="human" surface="white" className="gap-0">
              <h2 className="text-xl font-bold mb-4">
                {t("notFound.popular.features")}
              </h2>
              <ul className="space-y-2">
                <li>
                  <LinkTo
                    to="features.scheduling"
                    className="text-caire-action hover:underline"
                  >
                    {t("home:features.scheduling.title")}
                  </LinkTo>
                </li>
                <li>
                  <LinkTo
                    to="features.integrations"
                    className="text-caire-action hover:underline"
                  >
                    {t("home:features.integrations.title")}
                  </LinkTo>
                </li>
                <li>
                  <LinkTo
                    to="features.analytics"
                    className="text-caire-action hover:underline"
                  >
                    {t("home:features.analytics.title")}
                  </LinkTo>
                </li>
              </ul>
            </DomainCard>
            <DomainCard domain="human" surface="white" className="gap-0">
              <h2 className="text-xl font-bold mb-4">
                {t("notFound.popular.resources")}
              </h2>
              <ul className="space-y-2">
                <li>
                  <LinkTo
                    to="resourcesCategory.guides"
                    className="text-caire-action hover:underline"
                  >
                    {t("resources:categories.guides.title", "Guides")}
                  </LinkTo>
                </li>
                <li>
                  <LinkTo
                    to="faq"
                    className="text-caire-action hover:underline"
                  >
                    {t("notFound.popular.faq")}
                  </LinkTo>
                </li>
                <li>
                  <LinkTo
                    to="resourcesCategory.index"
                    className="text-caire-action hover:underline"
                  >
                    {t("notFound.popular.articles")}
                  </LinkTo>
                </li>
              </ul>
            </DomainCard>
            <DomainCard domain="human" surface="white" className="gap-0">
              <h2 className="text-xl font-bold mb-4">
                {t("notFound.popular.about")}
              </h2>
              <ul className="space-y-2">
                <li>
                  <LinkTo
                    to="about"
                    className="text-caire-action hover:underline"
                  >
                    {t("nav.about")}
                  </LinkTo>
                </li>
                <li>
                  <LinkTo
                    to="products"
                    className="text-caire-action hover:underline"
                  >
                    {t("nav.products")}
                  </LinkTo>
                </li>
                <li>
                  <LinkTo
                    to="contact"
                    className="text-caire-action hover:underline"
                  >
                    {t("nav.contact")}
                  </LinkTo>
                </li>
              </ul>
            </DomainCard>
          </div>
        </div>
      </div>
    </>
  );
};
